import { formatInteger } from "@/shared/utils/format";

import { useChartTip, useChartWidth } from "./chart-kit";
import { EmptyState } from "./EmptyState";

export type HeatCell = { day: number; hour: number; count: number };

/** Dom … Sáb, a convenção `DOW` do Postgres (0 = domingo). */
const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const LEVELS = ["Sem menção", "Baixo", "Médio-baixo", "Médio", "Alto", "Muito alto"];
const ROW = 26;
const PAD = { top: 18, left: 40, right: 4 };

/** O nível de 0 a 5 de uma contagem — os cinco degraus do v1 (o maior
 *  valor dividido em cinco faixas iguais), com zero à parte. */
export function heatLevel(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  return Math.min(5, Math.max(1, Math.ceil((count / max) * 5)));
}

/**
 * Mapa de intensidade: dia da semana × hora da publicação. Um tom só, do
 * claro ao escuro (magnitude), em seis degraus com legenda — e o número
 * em cada célula aparece na dica, nunca só pela cor.
 */
export function IntensityHeatmap({ cells }: { cells: HeatCell[] }) {
  const [frame, width] = useChartWidth<HTMLDivElement>();
  const { bind, tip } = useChartTip(frame);
  const grid = DAYS.map(() => Array.from({ length: 24 }, () => 0));
  for (const cell of cells) {
    if (cell.day >= 0 && cell.day < 7 && cell.hour >= 0 && cell.hour < 24) grid[cell.day][cell.hour] += cell.count;
  }
  const max = Math.max(...grid.flat(), 0);
  if (!max) return <EmptyState label="Sem publicações com horário neste recorte"/>;
  const cellWidth = Math.max(10, (width - PAD.left - PAD.right) / 24);
  const height = PAD.top + DAYS.length * ROW;
  return (
    <div className="chart-frame" ref={frame}>
      <svg data-export width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Menções por dia da semana e hora">
        {Array.from({ length: 24 }, (_, hour) => (
          hour % (cellWidth < 22 ? 3 : 1) === 0
            ? <text key={hour} x={PAD.left + hour * cellWidth + cellWidth / 2} y={11} className="chart-axis-text" textAnchor="middle">{`${hour}h`}</text>
            : null
        ))}
        {grid.map((row, day) => (
          <g key={day}>
            <text x={PAD.left - 8} y={PAD.top + day * ROW + ROW / 2} className="chart-label-text" textAnchor="end" dominantBaseline="middle">{DAYS[day]}</text>
            {row.map((count, hour) => (
              <rect
                key={hour}
                x={PAD.left + hour * cellWidth + 1}
                y={PAD.top + day * ROW + 1}
                width={cellWidth - 2}
                height={ROW - 2}
                rx={3}
                tabIndex={count ? 0 : -1}
                className={`chart-seq-${heatLevel(count, max)}`}
                aria-label={`${DAY_NAMES[day]}, ${hour}h: ${formatInteger(count)} menções`}
                {...bind({ title: `${DAY_NAMES[day]}, ${hour}h`, lines: [{ label: "Menções", value: formatInteger(count) }] })}
              />
            ))}
          </g>
        ))}
      </svg>
      {tip}
      <ul className="chart-legend" aria-label="Legenda">
        {LEVELS.map((label, level) => (
          <li key={label}><span className={`chart-legend-swatch chart-seq-${level}`} aria-hidden="true"/>{label}</li>
        ))}
      </ul>
    </div>
  );
}
